import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import LegalLayout from "@/components/site/LegalLayout";

const LAST_UPDATED = "December 2025";

const sections = [
  {
    id: "overview",
    title: "Overview",
    body: [
      "Rallie (\"we\", \"us\", \"our\") builds a padel tournament and live match scoring app for iOS and Android. This Privacy Policy explains what personal data we collect when you use the Rallie app and the rallie.app website, how we use it, and the choices you have.",
      "By using Rallie you agree to the collection and use of information in line with this policy. If you disagree, please stop using the app and website.",
    ],
  },
  {
    id: "data-we-collect",
    title: "Data we collect",
    body: [
      "We only collect what we need to run the product and improve it. Specifically:",
      {
        type: "list",
        items: [
          "Account data — your name, email, profile picture and optional home club when you sign up.",
          "Match & tournament data — the tournaments you create, players you add, scores, sets and results.",
          "Community data — connections you build with other Rallie players and any content you share (comments, avatars).",
          "Device data — model, OS version, language and crash logs so we can fix bugs.",
          "Usage data — anonymous, aggregated events (e.g. \"scored a point\") to understand which features are used.",
        ],
      },
      "We do not collect payment information because Rallie is free. We do not sell or rent your personal data.",
    ],
  },
  {
    id: "how-we-use",
    title: "How we use your data",
    body: [
      "We use your data to:",
      {
        type: "list",
        items: [
          "Create and secure your account, run tournaments, and calculate your stats.",
          "Show you relevant players from the community directory.",
          "Send transactional emails (account confirmations, deletion confirmations, replies to support tickets).",
          "Improve the product — fix bugs, optimise flows, plan new features.",
          "Prevent abuse, fraud and violations of our Terms of Use.",
        ],
      },
      "We never use your match data or identity for advertising.",
    ],
  },
  {
    id: "sharing",
    title: "Who we share data with",
    body: [
      "We share personal data with a small number of trusted service providers who help us run Rallie:",
      {
        type: "list",
        items: [
          "Cloud hosting (databases, application servers).",
          "Authentication providers (Apple, Google) if you sign in with them.",
          "Email delivery services for transactional emails.",
          "Crash and analytics providers (aggregated, no ad tracking).",
        ],
      },
      "Each provider is bound by contract to protect your data and only process it on our instructions. We may also disclose data if we are legally required to (e.g. valid court order) — never voluntarily.",
    ],
  },
  {
    id: "retention",
    title: "How long we keep your data",
    body: [
      "We keep your account data for as long as you have a Rallie account. If you delete your account (from Profile → Settings → Delete Account in the app, or via our /delete-account page), your personal data is removed from production systems immediately.",
      "Encrypted backups are automatically purged within 30 days. Anonymised match records that belong to a shared tournament may be retained so other participants keep their history intact — your name and identifying info are removed.",
      {
        type: "note",
        text: "Records required for legal, tax or fraud-prevention purposes may be retained up to the period required by applicable law.",
      },
    ],
  },
  {
    id: "your-rights",
    title: "Your rights",
    body: [
      "Depending on where you live, you have rights over your personal data. In particular:",
      {
        type: "list",
        items: [
          "Access — request a copy of the personal data we hold about you.",
          "Rectification — ask us to correct information that is wrong.",
          "Deletion — ask us to delete your account and data.",
          "Portability — receive your data in a machine-readable format.",
          "Objection — object to certain processing, or withdraw consent at any time.",
        ],
      },
      "Email privacy@rallie.app from your registered address to exercise any of these. We aim to respond within 30 days.",
    ],
  },
  {
    id: "children",
    title: "Children",
    body: [
      "Rallie is not directed at children under 13 (or under the equivalent minimum age in your jurisdiction). We do not knowingly collect personal data from children. If you believe a child has provided us with data, email privacy@rallie.app and we will delete it.",
    ],
  },
  {
    id: "security",
    title: "Security",
    body: [
      "We use encryption in transit (HTTPS/TLS) and at rest for stored data, along with role-based access controls, audit logging and regular security reviews. No system is 100% secure — please use a strong, unique password and email us immediately if you suspect unauthorised access.",
    ],
  },
  {
    id: "international",
    title: "International transfers",
    body: [
      "Rallie is used worldwide, so your data may be processed in countries other than the one you live in. When we transfer personal data across borders we use appropriate legal safeguards (e.g. Standard Contractual Clauses) to protect it.",
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: [
      "We may update this Privacy Policy from time to time. When we do, we will change the \"Last updated\" date at the top and, for material changes, notify you inside the app or by email. Continued use of Rallie after an update means you accept the new policy.",
    ],
  },
  {
    id: "contact",
    title: "Contact us",
    body: [
      "Questions or concerns about this policy or your data? Email privacy@rallie.app or open a ticket at rallie.app/support.",
    ],
  },
];

export default function Privacy() {
  return (
    <div className="bg-black text-white min-h-screen">
      <Nav />

      <div className="pt-28 max-w-4xl mx-auto px-6 md:px-10">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-400 hover:text-[#D4F34A] transition"
        >
          <ArrowLeft size={14} />
          Back to Rallie
        </Link>
      </div>

      <LegalLayout
        testId="privacy-page"
        eyebrow="Legal"
        title={
          <>
            Privacy
            <br />
            policy.
          </>
        }
        lastUpdated={LAST_UPDATED}
        intro="This policy explains what data Rallie collects, how we use it, who we share it with, and the rights you have over it. Read it in full — we've tried to keep it human."
        sections={sections}
      />

      <Footer />
    </div>
  );
}
